import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ActionIconButton, RHFSubmitBtn } from '@onesource/components';
import { FC, memo, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward, IoIosWarning } from 'react-icons/io';
import { MdCheck, MdErrorOutline, MdHelpOutline, MdHistory, MdRadioButtonUnchecked, MdSave } from 'react-icons/md';

import { STEPS, TAB_BG_COLOR, TFormIssue } from './steps';

const pulse = keyframes`
  0%, 100% {
    background-color: rgba(211, 47, 47, 0.12);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35);
  }
  50% {
    background-color: rgba(211, 47, 47, 0.55);
    box-shadow: 0 0 0 6px rgba(211, 47, 47, 0);
  }
`;

type Props = {
  activeStep: number;
  isPending: boolean;
  continueDisabled: boolean;
  saveDisabled: boolean;
  submitDisabled: boolean;
  allIssues: TFormIssue[];
  hasPendingRevision: boolean;
  onRevisionsClick: () => void;
  onBack: () => void;
  onContinue: () => void;
  onSaveDraft: () => void;
};

/** Question-icon button that toggles a popover listing every outstanding issue, grouped by step. */
const IssuesHelpButton: FC<{ issues: TFormIssue[] }> = ({ issues }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  if (issues.length === 0) {
    return null;
  }
  const open = !!anchorEl;
  const stepGroups = STEPS.map((step, index) => ({
    step,
    // Bucket any issue whose step isn't a real step index (e.g. -1) under the
    // first step so a counted issue can never be silently hidden from the list.
    issues: issues.filter(
      issue => issue.step === index || (index === 0 && (issue.step < 0 || issue.step >= STEPS.length)),
    ),
  })).filter(group => group.issues.length > 0);

  return (
    <>
      <Badge badgeContent={issues.length} color="error" overlap="circular">
        <ActionIconButton
          icon={<MdHelpOutline size={20} />}
          tooltip=""
          color="error"
          onClick={e => setAnchorEl(prev => (prev ? null : e.currentTarget))}
          sx={{
            animation: open ? 'none' : `${pulse} 1.6s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: '20px', boxShadow: '0px 3px 6px 0px rgba(0,0,0,0.08)' } } }}
      >
        <Box sx={{ p: 1.5, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
            In order to Submit the Application you must fix the following errors:
          </Typography>

          <Typography
            variant="caption"
            color="warning.dark"
            sx={{
              display: 'block',
              fontStyle: 'italic',
              mb: 1,
            }}
          >
            You can still Save Draft and finish later.
          </Typography>
          {stepGroups.map(({ step, issues: stepIssues }, groupIndex) => (
            <Box key={step.label} sx={{ mb: groupIndex < stepGroups.length - 1 ? 1.25 : 0 }}>
              <Chip
                icon={<step.icon size={12} style={{ marginRight: 0 }} />}
                label={step.label}
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  mb: 0.5,
                  bgcolor: theme => alpha(theme.palette.info.main, 0.08),
                  color: 'info.main',
                  '& .MuiChip-label': { px: 0.75 },
                  '& .MuiChip-icon': { color: 'info.main', ml: 0.75, fontSize: 12 },
                }}
              />
              <Stack>
                {stepIssues.map((issue, issueIndex) => (
                  <Stack
                    key={issue.message}
                    direction="row"
                    spacing={0.75}
                    alignItems="flex-start"
                    sx={{ marginTop: issueIndex > 0 ? '3px' : 0 }}
                  >
                    <Box
                      sx={{
                        color: issue.kind === 'invalid' ? 'error.main' : 'warning.main',
                        display: 'flex',
                        position: 'relative',
                        top: '3px',
                      }}
                    >
                      {issue.kind === 'invalid' ? <MdErrorOutline size={14} /> : <MdRadioButtonUnchecked size={12} />}
                    </Box>
                    <Typography variant="caption">
                      {issue.message}
                      {issue.kind === 'missing' && (
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: 9,
                            fontWeight: 700,
                            ml: 0.5,
                            bgcolor: theme => alpha(theme.palette.warning.main, 0.12),
                            color: 'warning.dark',
                            verticalAlign: 'middle',
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              {groupIndex < stepGroups.length - 1 && <Divider sx={{ mt: 1.25 }} />}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

const StepFooter: FC<Props> = ({
  activeStep,
  isPending,
  continueDisabled,
  saveDisabled,
  submitDisabled,
  allIssues,
  hasPendingRevision,
  onRevisionsClick,
  onBack,
  onContinue,
  onSaveDraft,
}) => {
  const isLastStep = activeStep === STEPS.length - 1;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const buttonSize = isMobile ? 'medium' : 'large';

  return (
    <Box
      sx={{
        flexShrink: 0,
        bgcolor: TAB_BG_COLOR,
        pt: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }} />

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}
      >
        {activeStep > 0 &&
          (isMobile ? (
            <ActionIconButton
              icon={<IoIosArrowBack size={20} />}
              tooltip="Back"
              disabled={isPending}
              onClick={onBack}
            />
          ) : (
            <Button
              size={buttonSize}
              variant="outlined"
              startIcon={<IoIosArrowBack />}
              onClick={onBack}
              color="primary"
              disabled={isPending}
            >
              Back
            </Button>
          ))}

        {isMobile ? (
          <Badge color="warning" variant="dot" invisible={!hasPendingRevision}>
            <ActionIconButton
              icon={<MdHistory size={20} />}
              tooltip="Revisions"
              color="primary"
              onClick={onRevisionsClick}
            />
          </Badge>
        ) : (
          <Button
            type="button"
            size={buttonSize}
            variant="outlined"
            startIcon={<MdHistory />}
            sx={{ pr: 4 }}
            endIcon={
              hasPendingRevision ? (
                <Box component="span" sx={{ color: 'warning.main', display: 'flex' }}>
                  <IoIosWarning size={16} />
                </Box>
              ) : undefined
            }
            onClick={onRevisionsClick}
          >
            Revisions
          </Button>
        )}

        {isMobile ? (
          <ActionIconButton
            icon={isPending ? <CircularProgress size={20} color="inherit" /> : <MdSave size={20} />}
            tooltip={isPending ? 'Saving...' : 'Save Draft'}
            color="primary"
            disabled={saveDisabled}
            onClick={onSaveDraft}
          />
        ) : (
          <Button
            type="button"
            size={buttonSize}
            variant="outlined"
            disabled={saveDisabled}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <MdSave />}
            onClick={onSaveDraft}
          >
            {isPending ? 'Saving...' : 'Save Draft'}
          </Button>
        )}

        {isLastStep ? (
          <>
            <IssuesHelpButton issues={allIssues} />
            <RHFSubmitBtn
              buttonProps={{
                size: buttonSize,
                disabled: submitDisabled,
                startIcon: isPending ? <CircularProgress size={16} color="inherit" /> : <MdCheck />,
              }}
            >
              {isPending ? 'Saving...' : 'Submit Application'}
            </RHFSubmitBtn>
          </>
        ) : (
          <>
            <IssuesHelpButton issues={allIssues} />
            <Button
              type="button"
              size={buttonSize}
              variant="contained"
              color="primary"
              disabled={continueDisabled}
              endIcon={<IoIosArrowForward />}
              onClick={onContinue}
            >
              {activeStep === STEPS.length - 2 ? 'Preview' : 'Continue'}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

/**
 * See ProDirectoryStepperHeader — same reasoning. `missingChips` is memoised by
 * content in tab.tsx so a shallow compare on it holds between keystrokes.
 */
export const ProDirectoryStepFooter = memo(StepFooter);
